/**
 * <custom-gender-hero> — hero dual WOMAN/MAN.
 *
 * El movimiento del track lo hace CSS puro desde html[data-custom-gender]
 * (ver custom_gender-hero.css); este componente solo:
 * - flechas: cambian al otro género vía window.customGender.set(..., 'hero')
 * - swipe táctil (<1150px): drag con rubber-band; al superar el umbral, set()
 * - videos: pausa los del slide inactivo, reproduce los del activo
 * - editor: seleccionar un bloque previsualiza su género
 */
const MOBILE_MQ = window.matchMedia('(max-width: 1149.98px)');
const SWIPE_THRESHOLD = 0.2;
const QUICK_SWIPE_THRESHOLD = 30;
const QUICK_SWIPE_TIME = 300;
const VERTICAL_SCROLL_THRESHOLD = 15;
const RUBBER_BAND = 0.3;

class CustomGenderHero extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track = this.querySelector('.custom_gender-hero__track');
    this.dual = this.classList.contains('custom_gender-hero--dual');
    if (!this.track) return;

    this.syncVideos();
    document.addEventListener('custom:gender:change', () => this.syncVideos(), { signal });

    this.querySelectorAll('.custom_gender-hero__arrow').forEach((arrow) => {
      arrow.addEventListener('click', () => this.setGender(this.other(), 'hero'), { signal });
    });

    if (this.dual) {
      this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true, signal });
      this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false, signal });
      this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true, signal });
      this.track.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    }

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.contains(event.target)) return;
        const slide = event.target.closest('.custom_gender-hero__slide');
        if (slide && slide.dataset.gender) this.setGender(slide.dataset.gender, 'editor');
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  current() {
    return document.documentElement.dataset.customGender === 'man' ? 'man' : 'woman';
  }

  other() {
    return this.current() === 'man' ? 'woman' : 'man';
  }

  setGender(gender, source) {
    if (window.customGender) window.customGender.set(gender, source);
  }

  syncVideos() {
    const current = this.current();
    this.querySelectorAll('.custom_gender-hero__slide').forEach((slide) => {
      const active = !this.dual || slide.dataset.gender === current;
      slide.querySelectorAll('video').forEach((video) => {
        if (active) {
          const played = video.play();
          if (played) played.catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  onTouchStart(event) {
    if (!MOBILE_MQ.matches) return;
    const touch = event.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.currentX = this.startX;
    this.startTime = Date.now();
    this.dragging = false;
    this.verticalScroll = false;
    this.baseOffset = this.current() === 'man' ? -100 : 0;
  }

  onTouchMove(event) {
    if (!MOBILE_MQ.matches || this.verticalScroll) return;
    const touch = event.touches[0];
    this.currentX = touch.clientX;
    const deltaX = this.currentX - this.startX;
    const deltaY = touch.clientY - this.startY;

    if (!this.dragging) {
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > VERTICAL_SCROLL_THRESHOLD) {
        this.verticalScroll = true;
        return;
      }
      if (Math.abs(deltaX) > VERTICAL_SCROLL_THRESHOLD) {
        this.dragging = true;
        this.track.classList.add('is-dragging');
      }
    }

    if (this.dragging) {
      event.preventDefault();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const width = this.track.offsetWidth;
        let offset = this.baseOffset + (deltaX / width) * 100;
        if (offset > 0) {
          offset *= RUBBER_BAND;
        } else if (offset < -100) {
          offset = -100 + (offset + 100) * RUBBER_BAND;
        }
        this.track.style.transform = `translateX(${offset}%)`;
      });
    }
  }

  onTouchEnd() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    if (!this.dragging) {
      this.verticalScroll = false;
      return;
    }
    const deltaX = this.currentX - this.startX;
    const width = this.track.offsetWidth;
    const duration = Date.now() - this.startTime;
    const isQuickSwipe = duration < QUICK_SWIPE_TIME && Math.abs(deltaX) > QUICK_SWIPE_THRESHOLD;
    const passedThreshold = Math.abs(deltaX) > width * SWIPE_THRESHOLD || isQuickSwipe;

    // Quitar el inline transform y re-habilitar la transición en el mismo frame:
    // el track anima desde la posición del drag hacia la de reposo (CSS).
    this.track.classList.remove('is-dragging');
    this.track.style.transform = '';

    if (passedThreshold) {
      const target = deltaX < 0 ? 'man' : 'woman';
      this.setGender(target, 'hero');
    }

    this.dragging = false;
    this.verticalScroll = false;
  }
}

if (!customElements.get('custom-gender-hero')) {
  customElements.define('custom-gender-hero', CustomGenderHero);
}
